import { CourseChanges } from '@/client/courses/types/course-changes.type';
import { LoadingStatuses } from '@/common/constants';
import {
  CourseChangesReview,
  PendingQuestionEdit,
  PendingSlideEdit,
} from '@/components/courses/course-changes';
import { AppButton, FlexContainer } from '@/ui';
import { GrayMagicPencilIcon } from '@/utils/helpers';
import { ProgressSpinner } from 'primereact/progressspinner';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const ReviewContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  position: relative;
`;

const ReviewContentCard = styled.div`
  background: var(--white-main);
  border-radius: 19px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ButtonContainer = styled.div`
  position: sticky;
  bottom: 0;
  padding-top: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 14px;
  align-items: center;
  z-index: 10;
`;

const IntroText = styled.div`
  font-family: var(--font-family);
  font-size: var(--medium-font-size);
  line-height: 1.4;
`;

export type CustomizeCourseReviewStepProps = {
  courseChanges?: CourseChanges;
  isLoadingChanges: boolean;
  pendingSlideEdits: PendingSlideEdit[];
  pendingQuestionEdits: PendingQuestionEdit[];
  onSlideEdit: (edit: PendingSlideEdit) => void;
  onQuestionEdit: (edit: PendingQuestionEdit) => void;
  onCancel: () => void;
  onContinue: () => void;
  onSave?: () => Promise<void> | void;
  isSaving?: boolean;
};

export const CustomizeCourseReviewStep: React.FC<
  CustomizeCourseReviewStepProps
> = ({
  courseChanges,
  isLoadingChanges,
  pendingSlideEdits,
  pendingQuestionEdits,
  onSlideEdit,
  onQuestionEdit,
  onCancel,
  onContinue,
  onSave,
  isSaving,
}) => {
  const { t } = useTranslation();

  const hasPendingEdits =
    pendingSlideEdits.length > 0 || pendingQuestionEdits.length > 0;

  return (
    <ReviewContentSection>
      {isLoadingChanges ? (
        <FlexContainer align="center" direction="column" width="100%" gap={13}>
          <ProgressSpinner
            style={{ width: '50px', height: '50px' }}
            strokeWidth="8"
          />
        </FlexContainer>
      ) : courseChanges ? (
        <>
          <FlexContainer
            align="center"
            justify="start"
            gap="var(--medium-padding)"
          >
            <GrayMagicPencilIcon />
            <IntroText>
              <b>{t('course.changes.intro.title')}</b>
              <br />
              {t('course.changes.intro.description')}
            </IntroText>
          </FlexContainer>
          <CourseChangesReview
            courseChanges={courseChanges}
            onSlideEdit={onSlideEdit}
            onQuestionEdit={onQuestionEdit}
            pendingSlideEdits={pendingSlideEdits}
            pendingQuestionEdits={pendingQuestionEdits}
            renderContainer={(children) => (
              <ReviewContentCard>{children}</ReviewContentCard>
            )}
          />
          <ButtonContainer>
            <AppButton
              label={t('course.customization.skipGuidedSteps')}
              onClick={onCancel}
              type="outlined"
            />
            {hasPendingEdits && (
              <AppButton
                label={t('button.save')}
                onClick={onSave}
                isDisabled={isSaving}
                state={
                  isSaving ? LoadingStatuses.LOADING : LoadingStatuses.IDLE
                }
              />
            )}
            <AppButton onClick={onContinue}>
              {t('course.customization.review.continueToPreview')}
            </AppButton>
          </ButtonContainer>
        </>
      ) : null}
    </ReviewContentSection>
  );
};
